import { AlertTriangle, CheckCircle2, Clock3 } from 'lucide-react';
import { useTranslation, type BillingSummary } from '@dukanti/shared';
import { cn } from '@/lib/utils';
import { formatBillingDate, overageLabelKey, stateKey } from '../lib/billing-formatters';

export function BillingStatusBanner({
  summary,
}: {
  readonly summary: BillingSummary;
}): React.JSX.Element {
  const { t, lang } = useTranslation();
  const entitlement = summary.entitlement;
  const state = entitlement.state;
  const isGood = entitlement.canUsePaidActions && state !== 'grace';
  const Icon = isGood ? CheckCircle2 : state === 'approval_pending' ? Clock3 : AlertTriangle;
  const deadline =
    state === 'grace'
      ? formatBillingDate(entitlement.graceEndsAt, lang)
      : formatBillingDate(entitlement.paidAccessUntil, lang);

  const message =
    state === 'grace' && deadline
      ? t('admin.billing.warning.pastDue', { deadline })
      : state === 'active'
        ? t('admin.billing.status.active')
        : t(stateKey(state));

  return (
    <section
      data-testid="billing-status-banner"
      className={cn(
        'rounded-card flex items-start gap-3 border p-4 text-start shadow-sm',
        isGood
          ? 'border-zaatar-soft bg-zaatar-wash text-zaatar-deep'
          : 'border-saffron/40 bg-saffron/10 text-sand-800',
      )}
    >
      <Icon className="mt-0.5 size-5 shrink-0" aria-hidden />
      <div className="min-w-0">
        <p className="font-medium">{t(stateKey(state))}</p>
        <p className="mt-1 text-sm leading-6">{message}</p>
        {summary.subscription?.paypalSubscriptionId ? (
          <p className="text-sand-500 mt-2 truncate text-xs" dir="ltr">
            {summary.subscription.paypalSubscriptionId}
          </p>
        ) : null}
        {entitlement.overages.length > 0 ? (
          <ul className="mt-2 space-y-1 text-sm">
            {entitlement.overages.map((overage) => (
              <li key={overage.gate}>{t(overageLabelKey(overage))}</li>
            ))}
          </ul>
        ) : null}
      </div>
    </section>
  );
}
